import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, Tag, Filter, Search } from "lucide-react";
import { COOKBOOKS } from "../data/cookbooks";

const DIFFICULTY_COLORS = {
  Beginner: "#15803D",
  Intermediate: "#BE185D",
  Advanced: "#7E22CE",
};

const DIFFICULTIES = ["All", "Beginner", "Intermediate", "Advanced"];

export default function Cookbooks() {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return COOKBOOKS.filter((c) => {
      if (filter !== "All" && c.difficulty !== filter) return false;
      if (q === "") return true;
      const haystack = [
        c.title,
        c.description,
        c.category,
        c.difficulty,
        ...(c.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [filter, search]);

  const loading = false;

  return (
    <div data-testid="cookbooks-page" className="bg-[#F5F5F0] min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="border-b border-[#D4D4D4] pb-12 mb-12">
          <p className="section-tag mb-3">&gt; COOKBOOKS.SYS</p>
          <h1 className="font-pixel text-3xl md:text-5xl text-[#0A0A0A] leading-tight mb-4">
            BUILD GUIDES
          </h1>
          <p className="font-terminal text-sm text-[#0A0A0A]/50 max-w-2xl leading-relaxed">
            Step-by-step technical guides for deploying AI systems with Melonai products. From quickstart to advanced orchestration patterns.
          </p>
        </div>

        {/* Difficulty Filters + Search */}
        <div className="flex flex-col lg:flex-row gap-4 mb-10">
          <div className="flex flex-wrap gap-2 items-center flex-1">
            <Filter size={14} className="text-[#0A0A0A]/30 self-center mr-2" />
            {DIFFICULTIES.map((diff) => (
              <button
                key={diff}
                onClick={() => setFilter(diff)}
                data-testid={`difficulty-filter-${diff.toLowerCase()}`}
                className={`font-arcade text-base uppercase tracking-wide px-4 py-2 border transition-colors duration-150 ${
                  filter === diff
                    ? "bg-[#4ADE80] text-black border-[#4ADE80]"
                    : "bg-transparent text-[#0A0A0A]/50 border-[#C8C8C8] hover:border-[#4ADE80] hover:text-[#15803D]"
                }`}
              >
                {diff}
              </button>
            ))}
          </div>
          <div className="relative lg:w-[300px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40 pointer-events-none" />
            <input
              type="text"
              placeholder="search cookbooks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="cookbooks-search"
              className="w-full font-terminal text-sm border border-[#C8C8C8] bg-[#F5F5F0] pl-9 pr-3 py-2 placeholder:text-[#0A0A0A]/40 focus:outline-none focus:border-[#4ADE80] focus:bg-white"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border-2 border-[#E5E5E5] h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((book) => (
              <Link
                key={book.id}
                to={`/cookbooks/${book.slug}`}
                className="block"
              >
              <article
                data-testid={`cookbook-${book.id}`}
                className="group bg-white border-2 border-[#D4D4D4] card-hover overflow-hidden flex flex-col h-full"
              >
                <div className="relative h-44 overflow-hidden bg-[#F5F5F0] border-b-2 border-[#B45309]/15 flex items-center justify-center">
                  <img
                    src={book.image_url}
                    alt={book.title}
                    className="w-full h-full object-cover [image-rendering:pixelated]"
                  />
                  <div className="absolute top-3 left-3">
                    <span
                      className="font-arcade text-base uppercase border px-2 py-1 bg-white/85"
                      style={{
                        color: DIFFICULTY_COLORS[book.difficulty] || "#15803D",
                        borderColor: DIFFICULTY_COLORS[book.difficulty] || "#15803D",
                      }}
                    >
                      {book.difficulty}
                    </span>
                  </div>
                  {book.status === "coming-soon" && (
                    <div className="absolute top-3 right-3 bg-white/90 border border-[#BE185D] px-2 py-1">
                      <span className="font-arcade text-xs text-[#BE185D] uppercase">SOON</span>
                    </div>
                  )}
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-4 mb-3 text-[#0A0A0A]/30">
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      <span className="font-terminal text-xs">{book.read_time} min read</span>
                    </span>
                  </div>

                  <h2 className="font-pixel text-xs text-[#0A0A0A] leading-snug mb-3 flex-1">
                    {book.title}
                  </h2>

                  <p className="font-terminal text-xs text-[#0A0A0A]/50 leading-relaxed mb-4 line-clamp-3">
                    {book.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {book.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="tag-chip text-sm flex items-center gap-1">
                        <Tag size={9} /> {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-end border-t border-[#E5E5E5] pt-4">
                    <span className="font-arcade text-sm text-[#15803D] group-hover:underline uppercase">
                      READ →
                    </span>
                  </div>
                </div>
              </article>
              </Link>
            ))}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <p className="font-arcade text-xl text-[#0A0A0A]/30 text-center py-20">
            No cookbooks match.
          </p>
        )}
      </div>
    </div>
  );
}
